import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Plus, Edit2, Trash2, Eye, X, Upload, MapPin, Users,
  Calendar, Search, Image, Film, Play,
} from 'lucide-react';
import { eventAPI } from '../../../../services/eventAPI';

/* ─────────────────────────────────────────────
   Portal CSS  (injected once into <head>)
───────────────────────────────────────────── */
const PORTAL_CSS = `
@keyframes emFadeUp  { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }
@keyframes emFadeIn  { from{opacity:0} to{opacity:1} }
@keyframes emSlideUp { from{opacity:0;transform:translateY(28px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
@keyframes emRowIn   { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }

/* ── Portal backdrop — rendered directly in <body> ── */
.em-portal-backdrop {
  position: fixed !important;
  inset: 0 !important;
  background: rgba(10,6,20,0.62) !important;
  backdrop-filter: blur(5px) !important;
  -webkit-backdrop-filter: blur(5px) !important;
  z-index: 999999 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 24px !important;
  animation: emFadeIn 0.18s ease both !important;
  overflow-y: auto !important;
}

.em-view-backdrop {
  position: fixed !important;
  inset: 0 !important;
  background: rgba(24,15,34,0.58) !important;
  backdrop-filter: blur(4px) !important;
  -webkit-backdrop-filter: blur(4px) !important;
  z-index: 999998 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 20px !important;
  animation: emFadeIn 0.18s ease both !important;
}

/* ── Form card ── */
.em-form-card {
  background: var(--surface);
  border-radius: 20px;
  width: 100%;
  max-width: 700px;
  max-height: 88vh;
  overflow-y: auto;
  box-shadow: 0 40px 100px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.07);
  animation: emSlideUp 0.28s cubic-bezier(0.22,1,0.36,1) both;
  position: relative;
  scrollbar-width: thin;
  scrollbar-color: var(--border) transparent;
}
.em-form-card::-webkit-scrollbar { width: 5px; }
.em-form-card::-webkit-scrollbar-track { background: transparent; }
.em-form-card::-webkit-scrollbar-thumb { background: var(--border); border-radius: 3px; }

/* ── Sticky header / footer ── */
.em-form-head {
  position: sticky; top: 0; z-index: 10;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  border-radius: 20px 20px 0 0;
  padding: 20px 26px 16px;
  display: flex; justify-content: space-between; align-items: center;
}
.em-form-foot {
  position: sticky; bottom: 0; z-index: 10;
  background: var(--surface);
  border-top: 1px solid var(--border);
  border-radius: 0 0 20px 20px;
  padding: 14px 26px;
  display: flex; gap: 10px; justify-content: flex-end;
}
.em-form-body { padding: 22px 26px; }

/* ── Close btn ── */
.em-close-btn {
  width: 32px; height: 32px; border-radius: 8px;
  border: 1px solid var(--border); background: transparent;
  color: var(--text-muted);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s; flex-shrink: 0;
}
.em-close-btn:hover { background: rgba(220,38,38,0.1); color: #ef4444; border-color: rgba(220,38,38,0.3); }

/* ── Pill toggles ── */
.em-pill {
  padding: 7px 16px; border-radius: 100px;
  font-size: 0.8rem; font-weight: 600; cursor: pointer;
  border: 1.5px solid var(--border); background: transparent;
  color: var(--text-secondary); transition: all 0.15s;
}
.em-pill.active { border-color: var(--brand); background: var(--brand); color: #fff; }
.em-pill:hover:not(.active) { border-color: var(--brand); color: var(--brand); }

/* ── Option cards (visibility / date fmt) ── */
.em-opt-card {
  flex: 1; padding: 10px 14px; border-radius: 10px;
  border: 1.5px solid var(--border); background: transparent;
  cursor: pointer; transition: all 0.15s;
  text-align: left; display: flex; flex-direction: column; gap: 2px;
}
.em-opt-card.active { border-color: var(--brand); background: rgba(134,27,104,0.06); }
.em-opt-card:hover:not(.active) { border-color: rgba(134,27,104,0.3); }

/* ── Cover zone ── */
.em-cover-zone {
  border: 2px dashed var(--border); border-radius: 14px;
  padding: 32px 20px; text-align: center; cursor: pointer;
  background: var(--surface-2); transition: border-color 0.2s; overflow: hidden;
}
.em-cover-zone:hover { border-color: var(--brand); }
.em-cover-zone.filled { padding: 0; }

/* ── Gallery ── */
.em-gallery-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 8px; margin-top: 8px;
}
.em-gallery-thumb {
  position: relative; border-radius: 10px; overflow: hidden;
  background: var(--surface-3); aspect-ratio: 1;
  border: 1.5px solid var(--border);
}
.em-gallery-add {
  border: 2px dashed var(--border); border-radius: 10px; aspect-ratio: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.2s; background: var(--surface-2); gap: 4px;
  color: var(--text-muted); font-size: 0.7rem; font-weight: 600;
}
.em-gallery-add:hover { border-color: var(--brand); color: var(--brand); background: rgba(134,27,104,0.05); }

/* ── Remove btn ── */
.em-rm-btn {
  position: absolute; top: 4px; right: 4px; width: 22px; height: 22px;
  border-radius: 50%; background: rgba(0,0,0,0.65); border: none;
  color: #fff; display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.15s; z-index: 2;
}
.em-rm-btn:hover { background: rgba(220,38,38,0.85); }

/* ── Video card ── */
.em-video-card {
  position: relative; border-radius: 10px; overflow: hidden;
  background: #0d0d0d; aspect-ratio: 16/9;
  border: 1.5px solid var(--border);
  display: flex; align-items: center; justify-content: center;
}

/* ── Upload / YT ── */
.em-upload-btn {
  display: flex; align-items: center; gap: 8px;
  border: 2px dashed var(--border); border-radius: 10px;
  padding: 12px 18px; background: var(--surface-2);
  cursor: pointer; color: var(--text-secondary); font-size: 0.84rem; font-weight: 600;
  transition: border-color 0.2s, color 0.2s; width: 100%;
  justify-content: center; font-family: var(--font);
}
.em-upload-btn:hover { border-color: var(--brand); color: var(--brand); }
.em-yt-row { display: flex; gap: 8px; align-items: stretch; }
.em-yt-input {
  flex: 1; padding: 10px 14px; border: 1.5px solid var(--border);
  border-radius: 10px; font-size: 0.84rem; background: var(--surface);
  color: var(--text-primary); outline: none; transition: border-color 0.2s;
  font-family: var(--font);
}
.em-yt-input:focus { border-color: var(--brand); }
.em-yt-add {
  padding: 10px 18px; background: var(--brand); color: #fff; border: none;
  border-radius: 10px; font-weight: 700; font-size: 0.84rem; cursor: pointer;
  white-space: nowrap; transition: opacity 0.15s; font-family: var(--font);
}
.em-yt-add:hover { opacity: 0.88; }

/* ── Section label ── */
.em-sect-label {
  font-size: 0.72rem; font-weight: 700; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 8px;
  display: flex; align-items: center; gap: 5px;
}

/* ── Divider ── */
.em-divider { height: 1px; background: var(--border); margin: 20px 0; opacity: 0.6; }

/* ── Badges ── */
.em-badge-up {
  background: rgba(16,185,129,0.1); color: #10b981;
  border: 1px solid rgba(16,185,129,0.22); padding: 3px 10px;
  border-radius: 100px; font-size: 0.72rem; font-weight: 700;
  display: inline-flex; align-items: center; gap: 4px;
}
.em-badge-past {
  background: var(--surface-3); color: var(--text-muted);
  border: 1px solid var(--border); padding: 3px 10px;
  border-radius: 100px; font-size: 0.72rem; font-weight: 700;
  display: inline-flex; align-items: center; gap: 4px;
}

/* ── Stat chip ── */
.em-chip {
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 0.74rem; color: var(--text-secondary);
  background: var(--surface-2); border: 1px solid var(--border);
  border-radius: 6px; padding: 2px 7px; font-weight: 500;
}

/* ── Table row hover ── */
.em-tr { transition: background 0.12s; }
.em-tr:hover { background: var(--surface-2) !important; }
`;

/* ─── helpers ─── */
function getImgSrc(ev) {
  if (ev?.imageSrc) return ev.imageSrc;
  if (ev?.imageData && ev?.imageMimeType) return `data:${ev.imageMimeType};base64,${ev.imageData}`;
  if (ev?.imageUrl) return ev.imageUrl;
  return null;
}

const Field = ({ label, hint, children }) => (
  <div className="form-group" style={{ marginBottom: 16 }}>
    <label style={{ display:'block', fontSize:'0.74rem', fontWeight:700, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:6 }}>
      {label}
      {hint && <span style={{ textTransform:'none', fontWeight:500, color:'var(--text-muted)', marginLeft:6, fontSize:'0.71rem' }}>{hint}</span>}
    </label>
    {children}
  </div>
);

/* ═══════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════ */
const EventManagement = ({ showNotification }) => {
  const [events,       setEvents]       = useState([]);
  const [filtered,     setFiltered]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [filterType,   setFilterType]   = useState('all');
  const [search,       setSearch]       = useState('');
  const [showForm,     setShowForm]     = useState(false);
  const [editingId,    setEditingId]    = useState(null);
  const [viewingEvent, setViewingEvent] = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [youtubeInput, setYoutubeInput] = useState('');

  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const coverFileRef   = useRef();
  const [galleryItems, setGalleryItems] = useState([]);
  const galleryFileRef = useRef();
  const [videoItems,   setVideoItems]   = useState([]);
  const videoFileRef   = useRef();

  const [formData, setFormData] = useState({
    title:'', description:'', date:'', location:'', city:'',
    venueLink:'', locationlink:'', scriptUrl:'',
    attendees:'', category:'upcoming', dateDisplayFormat:'full', isVisible:true,
  });

  /* inject CSS once */
  useEffect(() => {
    if (!document.getElementById('em-portal-css')) {
      const s = document.createElement('style');
      s.id = 'em-portal-css';
      s.textContent = PORTAL_CSS;
      document.head.appendChild(s);
    }
  }, []);

  /* lock body scroll */
  useEffect(() => {
    document.body.style.overflow = (showForm || viewingEvent) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showForm, viewingEvent]);

  useEffect(() => { fetchEvents(); }, [filterType]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(q ? events.filter(e =>
      e.title?.toLowerCase().includes(q) ||
      e.location?.toLowerCase().includes(q) ||
      e.description?.toLowerCase().includes(q)
    ) : events);
  }, [search, events]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const params = filterType === 'all' ? {} : { category: filterType };
      const res = await eventAPI.getAll(params);
      setEvents(res.success && res.data ? res.data : []);
    } catch (err) {
      setEvents([]);
      showNotification?.(`Error: ${err.message}`, 'error');
    } finally { setLoading(false); }
  };

  const handleCoverSelect = (e) => {
    const file = e.target.files[0]; if (!file) return;
    setImageFile(file);
    const r = new FileReader();
    r.onloadend = () => setImagePreview(r.result);
    r.readAsDataURL(file);
  };

  const handleGallerySelect = (e) => {
    Array.from(e.target.files).forEach(file => {
      const r = new FileReader();
      r.onloadend = () => setGalleryItems(p => [...p, { preview: r.result, file, isNew: true }]);
      r.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleVideoSelect = (e) => {
    Array.from(e.target.files).forEach(file => {
      const r = new FileReader();
      r.onloadend = () => setVideoItems(p => [...p, { preview: r.result, file, isNew: true, name: file.name, mimeType: file.type }]);
      r.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const handleAddYoutube = () => {
    const url = youtubeInput.trim(); if (!url) return;
    if (!url.includes('youtube.com') && !url.includes('youtu.be'))
      return showNotification?.('Please enter a valid YouTube URL', 'error');
    if (videoItems.length >= 5) return showNotification?.('Maximum 5 videos allowed', 'error');
    setVideoItems(p => [...p, { preview:null, file:null, isNew:true, isYoutube:true, youtubeUrl:url, name:'YouTube Video', mimeType:'video/youtube' }]);
    setYoutubeInput('');
  };

  const resetForm = () => {
    setFormData({ title:'', description:'', date:'', location:'', city:'', venueLink:'', locationlink:'', scriptUrl:'', attendees:'', category:'upcoming', dateDisplayFormat:'full', isVisible:true });
    setImageFile(null); setImagePreview(null);
    setGalleryItems([]); setVideoItems([]);
    setYoutubeInput(''); setEditingId(null); setShowForm(false);
  };

  const handleSave = async () => {
    if (!formData.title?.trim() || !formData.date)
      return showNotification?.('Title and date are required', 'error');
    try {
      setSaving(true);
      const fd = new FormData();
      [['locationlink', formData.locationlink||''], ['scriptUrl', formData.scriptUrl||''], ['title', formData.title], ['dateDisplayFormat', formData.dateDisplayFormat||'full'], ['description', formData.description||''], ['venueLink', formData.venueLink||''], ['isVisible', String(formData.isVisible!==false)], ['date', new Date(formData.date).toISOString()], ['location', formData.location||''], ['city', formData.city||''], ['attendees', String(parseInt(formData.attendees)||0)], ['category', new Date(formData.date)<new Date()?'past':'upcoming'], ['status','active']].forEach(([k,v]) => fd.append(k, v));
      if (imageFile) fd.append('image', imageFile);
      galleryItems.filter(i=>i.isNew).forEach(i => fd.append('galleryImages', i.file));
      const keepImg = galleryItems.filter(i=>!i.isNew).map(i=>i.existingData.index);
      if (editingId) fd.append('keepGalleryImageIndices', JSON.stringify(keepImg));
      videoItems.filter(v=>v.isNew&&!v.isYoutube).forEach(v => fd.append('galleryVideos', v.file));
      const ytLinks = videoItems.filter(v=>v.isNew&&v.isYoutube).map(v=>v.youtubeUrl);
      if (ytLinks.length) fd.append('youtubeLinks', JSON.stringify(ytLinks));
      const keepVid = videoItems.filter(v=>!v.isNew).map(v=>v.existingData?.index??v.existingIndex);
      if (editingId) fd.append('keepGalleryVideoIndices', JSON.stringify(keepVid));

      const res = editingId ? await eventAPI.updateWithFormData(editingId, fd) : await eventAPI.createWithFormData(fd);
      if (res.success) { showNotification?.(editingId ? 'Event updated!' : 'Event created!'); resetForm(); fetchEvents(); }
      else showNotification?.(`Failed: ${res.message}`, 'error');
    } catch (err) { showNotification?.(`Error: ${err.message}`, 'error'); }
    finally { setSaving(false); }
  };

  const handleEdit = (event) => {
    setShowForm(false);
    setTimeout(() => {
      setFormData({ title:event.title||'', description:event.description||'', date:event.date?new Date(event.date).toISOString().split('T')[0]:'', location:event.location||'', city:event.city||'', attendees:event.attendees||'', category:event.category||'upcoming', dateDisplayFormat:event.dateDisplayFormat||'full', venueLink:event.venueLink||'', locationlink:event.locationlink||'', scriptUrl:event.scriptUrl||'', isVisible:event.isVisible!==false });
      setImagePreview(getImgSrc(event)); setImageFile(null);
      setGalleryItems((event.gallery||[]).map((src,i)=>({ preview:src, isNew:false, existingData:{ index:i } })));
      setVideoItems((event.videos||[]).map((v,i)=>({ preview:v.src, isNew:false, name:v.name, mimeType:v.mimeType, isYoutube:v.isYoutube||false, youtubeUrl:v.youtubeUrl||null, existingData:{ index:i, mimeType:v.mimeType, name:v.name } })));
      setEditingId(event.id); setShowForm(true);
    }, 50);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    try {
      setLoading(true);
      const res = await eventAPI.delete(id);
      if (res.success) { showNotification?.('Event deleted'); if (viewingEvent?.id===id) setViewingEvent(null); fetchEvents(); }
      else showNotification?.(`Failed: ${res.message}`, 'error');
    } catch (err) { showNotification?.(`Error: ${err.message}`, 'error'); }
    finally { setLoading(false); }
  };

  const counts = { all:events.length, upcoming:events.filter(e=>e.category==='upcoming').length, past:events.filter(e=>e.category==='past').length };

  /* ════════════════════════════════════════
     FORM MODAL — portalled into document.body
  ════════════════════════════════════════ */
  const FormModal = showForm && ReactDOM.createPortal(
    <div className="em-portal-backdrop" onClick={e => e.target===e.currentTarget && resetForm()}>
      <div className="em-form-card">

        {/* sticky header */}
        <div className="em-form-head">
          <div>
            <div style={{ fontSize:'1.02rem', fontWeight:700, color:'var(--text-primary)' }}>
              {editingId ? '✏️ Edit Event' : '✨ New Event'}
            </div>
            <div style={{ fontSize:'0.74rem', color:'var(--text-muted)', marginTop:2 }}>
              {editingId ? 'Update event details and media' : 'Fill in the details to create a new event'}
            </div>
          </div>
          <button className="em-close-btn" onClick={resetForm}><X size={15} /></button>
        </div>

        {/* body */}
        <div className="em-form-body">

          <div className="form-grid-2">
            <Field label="Event Title" hint="*">
              <input type="text" placeholder="e.g. Annual Hospitality Summit" value={formData.title} onChange={e=>setFormData({...formData,title:e.target.value})} />
            </Field>
            <Field label="Date" hint="*">
              <input type="date" value={formData.date} onChange={e=>setFormData({...formData,date:e.target.value})} />
            </Field>
          </div>

          <Field label="Visibility">
            <div style={{ display:'flex', gap:8 }}>
              {[{v:true,l:'👁 Visible',d:'Shown on website'},{v:false,l:'🚫 Hidden',d:'Not shown on website'}].map(o=>(
                <button key={String(o.v)} className={`em-opt-card${formData.isVisible===o.v?' active':''}`} onClick={()=>setFormData({...formData,isVisible:o.v})}>
                  <span style={{fontSize:'0.84rem',fontWeight:600,color:'var(--text-primary)'}}>{o.l}</span>
                  <span style={{fontSize:'0.7rem',color:'var(--text-muted)'}}>{o.d}</span>
                </button>
              ))}
            </div>
          </Field>

          <Field label="Description">
            <textarea placeholder="Describe what this event is about…" value={formData.description} onChange={e=>setFormData({...formData,description:e.target.value})} style={{minHeight:84}} />
          </Field>

          <div className="form-grid-2">
            <Field label="Venue Name">
              <input type="text" placeholder="e.g. Taj Hotel, Ballroom" value={formData.location} onChange={e=>setFormData({...formData,location:e.target.value})} />
            </Field>
            <Field label="City" hint="(shown in city tabs)">
              <input type="text" placeholder="e.g. New Delhi" value={formData.city} onChange={e=>setFormData({...formData,city:e.target.value})} />
            </Field>
          </div>

          <Field label="Location Google Maps Link" hint="(shown on past event cards)">
            <input type="text" placeholder="https://maps.app.goo.gl/..." value={formData.locationlink} onChange={e=>setFormData({...formData,locationlink:e.target.value})} />
          </Field>

          {formData.category==='upcoming' && (
            <Field label="RSVP Form Script URL" hint="(Google Apps Script for this city)">
              <input type="text" placeholder="https://script.google.com/macros/s/..." value={formData.scriptUrl} onChange={e=>setFormData({...formData,scriptUrl:e.target.value})} />
              {formData.scriptUrl && (
                <div style={{marginTop:6,padding:'7px 12px',background:'rgba(16,185,129,0.08)',borderRadius:8,fontSize:'0.77rem',color:'#10b981',border:'1px solid rgba(16,185,129,0.2)',display:'flex',alignItems:'center',gap:6}}>
                  ✓ RSVP link set
                </div>
              )}
            </Field>
          )}

          <div className="form-grid-2">
            <Field label="Expected Attendees">
              <input type="number" placeholder="0" value={formData.attendees} onChange={e=>setFormData({...formData,attendees:e.target.value})} />
            </Field>
            <Field label="Category">
              <div style={{display:'flex',gap:8,marginTop:4}}>
                {['upcoming','past'].map(cat=>(
                  <button key={cat} className={`em-pill${formData.category===cat?' active':''}`} onClick={()=>setFormData({...formData,category:cat})}>
                    {cat==='upcoming'?'● Upcoming':'○ Past'}
                  </button>
                ))}
              </div>
            </Field>
          </div>

          <Field label="Date Display Format">
            <div style={{display:'flex',gap:8}}>
              {[{v:'full',l:'📅 Full Date',ex:'27 March 2026'},{v:'month-year',l:'🗓 Month & Year',ex:'March 2026'}].map(o=>(
                <button key={o.v} className={`em-opt-card${formData.dateDisplayFormat===o.v?' active':''}`} onClick={()=>setFormData({...formData,dateDisplayFormat:o.v})}>
                  <span style={{fontSize:'0.83rem',fontWeight:600,color:'var(--text-primary)'}}>{o.l}</span>
                  <span style={{fontSize:'0.69rem',color:'var(--text-muted)',marginTop:2}}>{o.ex}</span>
                </button>
              ))}
            </div>
          </Field>

          <div className="em-divider" />

          {/* Cover Image */}
          <Field label="Cover Image">
            <div className={`em-cover-zone${imagePreview?' filled':''}`} onClick={()=>coverFileRef.current?.click()}>
              {imagePreview ? (
                <div style={{position:'relative'}}>
                  <img src={imagePreview} alt="cover" style={{width:'100%',height:148,objectFit:'cover',display:'block'}} />
                  <button onClick={e=>{e.stopPropagation();setImagePreview(null);setImageFile(null);}}
                    style={{position:'absolute',top:8,right:8,background:'rgba(0,0,0,0.6)',border:'none',borderRadius:'50%',width:28,height:28,color:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <X size={13}/>
                  </button>
                  <div style={{position:'absolute',bottom:8,left:8,background:'rgba(0,0,0,0.5)',borderRadius:6,padding:'3px 8px',fontSize:'0.69rem',color:'#fff'}}>Click to replace</div>
                </div>
              ) : (
                <>
                  <Upload size={22} style={{color:'var(--text-muted)',marginBottom:8}} />
                  <p style={{fontSize:'0.85rem',color:'var(--text-secondary)',margin:0,fontWeight:600}}>Click to upload cover image</p>
                  <p style={{fontSize:'0.73rem',color:'var(--text-muted)',marginTop:4}}>PNG, JPG, WEBP · up to 5 MB</p>
                </>
              )}
            </div>
            <input ref={coverFileRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleCoverSelect} />
          </Field>

          {/* Gallery */}
          <Field label={`Photo Gallery (${galleryItems.length}/20)`}>
            <div className="em-gallery-grid">
              {galleryItems.map((item,idx)=>(
                <div key={idx} className="em-gallery-thumb">
                  <img src={item.preview} alt="" style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}} />
                  <button className="em-rm-btn" onClick={()=>setGalleryItems(p=>p.filter((_,i)=>i!==idx))}><X size={11}/></button>
                </div>
              ))}
              {galleryItems.length<20 && (
                <div className="em-gallery-add" onClick={()=>galleryFileRef.current?.click()}>
                  <Plus size={18}/><span>Add photos</span>
                </div>
              )}
            </div>
            <input ref={galleryFileRef} type="file" accept="image/*" multiple style={{display:'none'}} onChange={handleGallerySelect} />
            <p style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:7}}>Up to 20 images. Users can open fullscreen gallery.</p>
          </Field>

          {/* Videos */}
          <Field label={`Videos (${videoItems.length}/5)`}>
            {videoItems.length>0 && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(128px,1fr))',gap:8,marginBottom:10}}>
                {videoItems.map((item,idx)=>(
                  <div key={idx} className="em-video-card">
                    {item.isYoutube
                      ? <div style={{width:'100%',height:'100%',background:'#ff0000',display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:4}}><span style={{fontSize:24}}>▶</span><span style={{color:'#fff',fontSize:'0.63rem'}}>YouTube</span></div>
                      : <video src={item.preview} style={{width:'100%',height:'100%',objectFit:'cover'}} muted />
                    }
                    <div style={{position:'absolute',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.3)'}}><Play size={18} style={{color:'#fff'}}/></div>
                    <button className="em-rm-btn" onClick={()=>setVideoItems(p=>p.filter((_,i)=>i!==idx))}><X size={11}/></button>
                    <div style={{position:'absolute',bottom:4,left:4,fontSize:'0.62rem',color:'#fff',background:'rgba(0,0,0,0.55)',borderRadius:4,padding:'2px 5px',maxWidth:'82%',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                      {item.isYoutube?'🎬 YouTube':item.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {videoItems.length<5 && (
              <div style={{display:'flex',flexDirection:'column',gap:8}}>
                <button className="em-upload-btn" onClick={()=>videoFileRef.current?.click()}><Film size={16}/> Upload Video File</button>
                <div className="em-yt-row">
                  <input className="em-yt-input" type="text" placeholder="Or paste YouTube link: https://youtube.com/watch?v=..." value={youtubeInput} onChange={e=>setYoutubeInput(e.target.value)} onKeyDown={e=>e.key==='Enter'&&handleAddYoutube()} />
                  <button className="em-yt-add" onClick={handleAddYoutube}>Add</button>
                </div>
              </div>
            )}
            <input ref={videoFileRef} type="file" accept="video/*" multiple style={{display:'none'}} onChange={handleVideoSelect} />
            <p style={{fontSize:'0.72rem',color:'var(--text-muted)',marginTop:7}}>Upload MP4/MOV or paste a YouTube link. Max 5 videos total.</p>
          </Field>

        </div>{/* /body */}

        {/* sticky footer */}
        <div className="em-form-foot">
          <button className="btn-secondary" onClick={resetForm} style={{minWidth:88}}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving} style={{minWidth:138}}>
            {saving?'Saving…':editingId?'Update Event':'Create Event'}
          </button>
        </div>

      </div>
    </div>,
    document.body   /* ← teleported: escapes ALL stacking contexts */
  );

  /* ════════════════════════════════════════
     VIEW MODAL — portalled into document.body
  ════════════════════════════════════════ */
  const ViewModal = viewingEvent && ReactDOM.createPortal((() => {
    const src = getImgSrc(viewingEvent);
    const isPast = viewingEvent.category==='past'||new Date(viewingEvent.date)<new Date();
    const imgCount = viewingEvent.gallery?.length||0;
    const vidCount = viewingEvent.videos?.length||0;
    return (
      <div className="em-view-backdrop" onClick={e=>e.target===e.currentTarget&&setViewingEvent(null)}>
        <div className="modal-card">
          <div className="modal-header">
            <h3>Event Details</h3>
            <button onClick={()=>setViewingEvent(null)} style={{background:'none',border:'none',color:'var(--text-muted)',cursor:'pointer'}}><X size={18}/></button>
          </div>
          {src
            ? <img src={src} alt={viewingEvent.title} style={{width:'100%',height:200,objectFit:'cover',display:'block'}} />
            : <div style={{width:'100%',height:120,background:'linear-gradient(135deg,var(--brand-muted),var(--surface-3))',display:'flex',alignItems:'center',justifyContent:'center'}}><Image size={36} style={{color:'var(--brand)',opacity:0.4}}/></div>
          }
          <div className="modal-body">
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
              <h2 style={{fontSize:'1.1rem',fontWeight:700,color:'var(--text-primary)',flex:1}}>{viewingEvent.title}</h2>
              <span className={isPast?'em-badge-past':'em-badge-up'}>{isPast?'○ Past':'● Upcoming'}</span>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16,fontSize:'0.875rem',color:'var(--text-secondary)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}><Calendar size={14} style={{color:'var(--brand)'}}/>{new Date(viewingEvent.date).toLocaleDateString('en-IN',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</div>
              {viewingEvent.location && <div style={{display:'flex',alignItems:'center',gap:8}}><MapPin size={14} style={{color:'var(--brand)'}}/>{viewingEvent.location}{viewingEvent.city?` · ${viewingEvent.city}`:''}</div>}
              {viewingEvent.attendees>0 && <div style={{display:'flex',alignItems:'center',gap:8}}><Users size={14} style={{color:'var(--brand)'}}/>{Number(viewingEvent.attendees).toLocaleString()} expected attendees</div>}
              <div style={{display:'flex',gap:8}}>
                {imgCount>0 && <span className="em-chip"><Image size={11}/>{imgCount} photo{imgCount!==1?'s':''}</span>}
                {vidCount>0 && <span className="em-chip"><Film size={11}/>{vidCount} video{vidCount!==1?'s':''}</span>}
              </div>
            </div>
            {viewingEvent.description && (
              <div>
                <div style={{fontSize:'0.7rem',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:8}}>Description</div>
                <p style={{fontSize:'0.875rem',color:'var(--text-secondary)',lineHeight:1.75}}>{viewingEvent.description}</p>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn-danger" onClick={()=>{setViewingEvent(null);handleDelete(viewingEvent.id);}}><Trash2 size={13}/> Delete</button>
            <button className="btn-secondary" onClick={()=>setViewingEvent(null)}>Close</button>
            <button className="btn-primary" onClick={()=>{setViewingEvent(null);handleEdit(viewingEvent);}}><Edit2 size={13}/> Edit Event</button>
          </div>
        </div>
      </div>
    );
  })(), document.body);

  /* ════════════════════════════════════════
     PAGE RENDER
  ════════════════════════════════════════ */
  return (
    <div style={{display:'flex',flexDirection:'column',animation:'emFadeUp 0.38s cubic-bezier(0.22,1,0.36,1) both'}}>

      {/* Modals rendered via portals — outside this div, directly in <body> */}
      {FormModal}
      {ViewModal}

      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-end',marginBottom:20,flexWrap:'wrap',gap:12}}>
        <div>
          <h2 style={{fontSize:'1.5rem',fontWeight:750,color:'var(--text-primary)',letterSpacing:'-0.03em',lineHeight:1.1}}>Events</h2>
          <p style={{fontSize:'0.82rem',color:'var(--text-muted)',marginTop:4}}>Manage upcoming and past events with photo & video galleries</p>
        </div>
        <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:10,padding:'0 14px',height:38,minWidth:210,transition:'border-color 0.2s'}}
            onFocus={e=>e.currentTarget.style.borderColor='var(--brand)'}
            onBlur={e=>e.currentTarget.style.borderColor='var(--border)'}
          >
            <Search size={14} style={{color:'var(--text-muted)',flexShrink:0}} />
            <input style={{border:'none',outline:'none',background:'transparent',fontSize:'0.85rem',color:'var(--text-primary)',width:'100%'}}
              placeholder="Search events…" value={search} onChange={e=>setSearch(e.target.value)} />
            {search && <button onClick={()=>setSearch('')} style={{background:'none',border:'none',cursor:'pointer',color:'var(--text-muted)',lineHeight:1,padding:0}}><X size={13}/></button>}
          </div>
          <button className="btn-primary" onClick={()=>{resetForm();setShowForm(true);}} style={{whiteSpace:'nowrap',display:'flex',alignItems:'center',gap:6}}>
            <Plus size={15}/> New Event
          </button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div style={{display:'flex',gap:6,marginBottom:18,alignItems:'center',flexWrap:'wrap'}}>
        {[{k:'all',l:'All Events'},{k:'upcoming',l:'● Upcoming'},{k:'past',l:'○ Past'}].map(t=>(
          <button key={t.k} className={`filter-btn${filterType===t.k?' active':''}`} onClick={()=>setFilterType(t.k)}
            style={{display:'flex',alignItems:'center',gap:6}}>
            {t.l}
            <span style={{background:filterType===t.k?'rgba(255,255,255,0.22)':'var(--surface-3)',color:filterType===t.k?'#fff':'var(--text-muted)',borderRadius:10,fontSize:'0.7rem',fontWeight:700,padding:'1px 7px'}}>
              {counts[t.k]}
            </span>
          </button>
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="empty-state"><p style={{color:'var(--text-muted)'}}>Loading events…</p></div>
      ) : filtered.length===0 ? (
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:16,overflow:'hidden'}}>
          <div className="empty-state">
            <div className="empty-state-icon"><Calendar size={22}/></div>
            <h3>{search?'No events match your search':'No events yet'}</h3>
            <p>{search?'Try a different keyword':'Create your first event using the button above'}</p>
          </div>
        </div>
      ) : (
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:16,overflow:'hidden',boxShadow:'var(--shadow-sm)'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr style={{background:'var(--surface-2)'}}>
                {['Event','Date','Location','Gallery','Attendees','Status','Actions'].map(h=>(
                  <th key={h} style={{padding:'11px 16px',textAlign:'left',fontSize:'0.68rem',fontWeight:700,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.08em',borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((event,i)=>{
                const src = getImgSrc(event);
                const isPast = event.category==='past'||new Date(event.date)<new Date();
                const imgCount = event.gallery?.length||0;
                const vidCount = event.videos?.length||0;
                return (
                  <tr key={event.id} className="em-tr" style={{animation:`emRowIn 0.26s ease ${i*0.035}s both`}}>
                    <td style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',verticalAlign:'middle'}}>
                      <div style={{display:'flex',alignItems:'center',gap:11}}>
                        {src
                          ? <img src={src} alt={event.title} style={{width:42,height:42,borderRadius:9,objectFit:'cover',border:'1.5px solid var(--border)',flexShrink:0}} />
                          : <div style={{width:42,height:42,borderRadius:9,background:'var(--brand-muted)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,border:'1.5px solid var(--border)'}}><Image size={15} style={{color:'var(--brand)'}}/></div>
                        }
                        <div style={{minWidth:0}}>
                          <div style={{fontWeight:650,fontSize:'0.875rem',color:'var(--text-primary)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:200}}>{event.title}</div>
                          {event.description && <div style={{fontSize:'0.73rem',color:'var(--text-muted)',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',marginTop:2}}>{event.description}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',verticalAlign:'middle',whiteSpace:'nowrap'}}>
                      <div style={{fontSize:'0.84rem',fontWeight:600}}>{new Date(event.date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</div>
                    </td>
                    <td style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',verticalAlign:'middle'}}>
                      {event.location
                        ? <div style={{display:'flex',alignItems:'center',gap:5,fontSize:'0.84rem',color:'var(--text-secondary)'}}><MapPin size={12} style={{color:'var(--brand)',flexShrink:0}}/>{event.location}</div>
                        : <span style={{color:'var(--text-muted)',fontSize:'0.8rem'}}>—</span>}
                    </td>
                    <td style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',verticalAlign:'middle'}}>
                      <div style={{display:'flex',flexDirection:'column',gap:3}}>
                        {imgCount>0 && <span className="em-chip"><Image size={10}/>{imgCount} photo{imgCount!==1?'s':''}</span>}
                        {vidCount>0 && <span className="em-chip"><Film size={10}/>{vidCount} video{vidCount!==1?'s':''}</span>}
                        {!imgCount&&!vidCount && <span style={{color:'var(--text-muted)',fontSize:'0.8rem'}}>—</span>}
                      </div>
                    </td>
                    <td style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',verticalAlign:'middle'}}>
                      {event.attendees>0
                        ? <div style={{display:'flex',alignItems:'center',gap:5,fontSize:'0.84rem'}}><Users size={12} style={{color:'var(--text-muted)'}}/>{Number(event.attendees).toLocaleString()}</div>
                        : <span style={{color:'var(--text-muted)'}}>—</span>}
                    </td>
                    <td style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',verticalAlign:'middle'}}>
                      <span className={isPast?'em-badge-past':'em-badge-up'}>{isPast?'○ Past':'● Upcoming'}</span>
                    </td>
                    <td style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',verticalAlign:'middle'}}>
                      <div className="table-actions">
                        <button className="icon-btn"        title="View"   onClick={()=>setViewingEvent(event)}><Eye    size={13}/></button>
                        <button className="icon-btn edit"   title="Edit"   onClick={()=>handleEdit(event)}><Edit2  size={13}/></button>
                        <button className="icon-btn danger" title="Delete" onClick={()=>handleDelete(event.id)}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EventManagement;